import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { SkeletonTheme } from 'react-loading-skeleton';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import HelpFeedbackButtons from './components/HelpFeedbackButtons';
import { ThemeProvider } from './ThemeContext';
import { PwaInstallProvider } from './PwaInstallContext';
import BlockedUserGate from './components/BlockedUserGate';
import Spinner from './components/Spinner';

const Home = lazy(() => import('./components/Home'));
const StartSheet = lazy(() => import('./components/StartSheet'));
const AddPersons = lazy(() => import('./components/AddPersons'));
const SheetPage = lazy(() => import('./components/SheetPage'));
const SignUp = lazy(() => import('./components/SignUp'));
const Login = lazy(() => import('./components/Login'));
const SettlementsPage = lazy(() => import('./components/SettlementsPage'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const SharedSheetView = lazy(() => import('./components/SharedSheetView'));
const SharedSettlementsView = lazy(() => import('./components/SharedSettlementsView'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const CompleteProfile = lazy(() => import('./components/CompleteProfile'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const MyRequests = lazy(() => import('./components/MyRequests'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const AdminSuperPasswordGate = lazy(() => import('./components/AdminSuperPasswordGate'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const useUserExistenceGuard = () => {
  const [user] = useAuthState(auth);
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    const authPaths = [
      '/complete-profile',
      '/login',
      '/signup',
      '/forgot-password',
      '/reset-password',
      '/admin',
      '/my-requests',
      '/contact-us',
      '/profile',
    ];
    if (authPaths.includes(location.pathname)) return;

    const q = query(collection(db, 'users'), where('uid', '==', user.uid));
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) {
        signOut(auth);
      }
    });

    return () => unsub();
  }, [user, location.pathname]);
};

const AppLayout = () => {
  const location = useLocation();
  const path = location.pathname;
  const isHomePage = path === '/';
  useUserExistenceGuard();

  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      prevent: (node) => {
        let el = node;
        while (el && el !== document.body) {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY;
          if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            el.scrollHeight > el.clientHeight
          ) {
            return true;
          }
          el = el.parentElement;
        }
        return false;
      },
    });

    let raf;
    const animate = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BlockedUserGate>
      <SkeletonTheme baseColor="var(--glass-bg)" highlightColor="var(--glass-border)">
        <div className="App">
          <ScrollToTop />
          <Navbar />
          <div className={isHomePage ? 'main-content-full' : 'main-content'}>
            <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{minHeight:'60vh'}}><Spinner /></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/start" element={<ProtectedRoute><StartSheet /></ProtectedRoute>} />
              <Route path="/add-persons/:sheetId" element={<ProtectedRoute><AddPersons /></ProtectedRoute>} />
              <Route path="/sheet/:sheetId" element={<ProtectedRoute><SheetPage /></ProtectedRoute>} />
              <Route path="/settlements/:sheetId" element={<ProtectedRoute><SettlementsPage /></ProtectedRoute>} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/shared/:shareToken" element={<SharedSheetView />} />
              <Route path="/shared/:shareToken/settlements" element={<SharedSettlementsView />} />
              <Route path="/view/:viewOnlyToken" element={<SharedSheetView viewOnly />} />
              <Route path="/view/:viewOnlyToken/settlements" element={<SharedSettlementsView viewOnly />} />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminSuperPasswordGate>
                      <AdminDashboard />
                    </AdminSuperPasswordGate>
                  </AdminRoute>
                }
              />
              <Route path="/my-requests" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />
            </Routes>
            </Suspense>
          </div>
          <HelpFeedbackButtons />
          <Footer />
        </div>
      </SkeletonTheme>
    </BlockedUserGate>
  );
};

function App() {
  return (
    <ThemeProvider>
      <PwaInstallProvider>
        <Router>
          <AppLayout />
        </Router>
      </PwaInstallProvider>
    </ThemeProvider>
  );
}

export default App;
